import { apiRequest } from './client'
import {
  openAgentConfirmStream,
  openAgentRunStream,
  type AgentConfirmStreamBody,
  type AgentRunStreamHandle,
  type AgentRunStreamHandlers,
} from '../lib/agentRunStream'

export type AgentMode = 'auto' | 'react' | 'plan_execute'

export type AgentPendingAction = {
  token: string
  toolName: string
  input: Record<string, unknown>
  assistantThought: string
  runPublicId?: string | null
}

export type AgentRunResponse = {
  runPublicId?: string | null
  status: 'succeeded' | 'failed' | 'awaiting_confirmation' | 'cancelled'
  goal: string
  requestedMode: AgentMode
  selectedMode: Exclude<AgentMode, 'auto'> | AgentMode
  context: Record<string, unknown>
  plan: Record<string, unknown>
  output: Record<string, unknown>
  pendingAction: AgentPendingAction | null
  createdAt: string
}

export type AgentToolDescriptor = {
  name: string
  /** 工具分组，如 comic / reference / timeline；mcp 分组表示来自 MCP 服务端 */
  group?: string
  description: string
  parameters: Record<string, unknown>
  requiresConfirmation: boolean
}

/** 智能体技能摘要（斜杠引用菜单；正文随运行注入，不经前端） */
export type AgentSkill = {
  publicId: string
  name: string
  description: string
}

export type AgentRunRequest = {
  goal: string
  mode?: AgentMode
  model?: string
  autoConfirm?: boolean
  context?: Record<string, unknown>
  /** 本次运行禁用的工具名列表；缺省 = 全部启用 */
  disabledTools?: string[]
  /** 本次运行引用的技能 publicId 列表（斜杠引用）；缺省 = 不引用技能 */
  skillIds?: string[]
}

export type AgentRunSummary = {
  runPublicId: string
  status: string
  goal: string
  requestedMode: string
  selectedMode: string | null
  model: string | null
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

export type AgentRunStep = {
  stepIndex: number
  title: string
  status: string
  note: string | null
  updatedAt: string
}

export type AgentRunDetail = {
  runPublicId: string
  status: string
  goal: string
  requestedMode: string
  selectedMode: string | null
  model: string | null
  context: Record<string, unknown>
  plan: Record<string, unknown>
  output: Record<string, unknown>
  errorMessage: string | null
  steps: AgentRunStep[]
  createdAt: string
  updatedAt: string
}

export type AgentRunListResponse = {
  items: AgentRunSummary[]
  total: number
  page: number
  size: number
}

export type { AgentRunStreamHandle, AgentRunStreamHandlers }

export function createAgentRun(body: AgentRunRequest) {
  return apiRequest<AgentRunResponse>('/v1/agents/runs', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function listAgentRuns(page = 1, size = 20) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  })
  return apiRequest<AgentRunListResponse>(`/v1/agents/runs?${params}`, { method: 'GET' })
}

export function getAgentRun(runPublicId: string) {
  return apiRequest<AgentRunDetail>(`/v1/agents/runs/${encodeURIComponent(runPublicId)}`, {
    method: 'GET',
  })
}

export function confirmAgentPendingAction(body: {
  token: string
  approved?: boolean
  runPublicId?: string | null
}) {
  return apiRequest<AgentRunResponse>('/v1/agents/pending-actions/confirm', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function listAgentTools() {
  return apiRequest<AgentToolDescriptor[]>('/v1/agents/tools', { method: 'GET' })
}

/** 列出启用中的技能（斜杠引用菜单） */
export function listAgentSkills() {
  return apiRequest<AgentSkill[]>('/v1/agents/skills', { method: 'GET' })
}

/** 智能体生图卡片「重新生成」：按原任务轮次复用参数重新入队 */
export function retryAgentGeneration(taskId: string) {
  return apiRequest<{
    status: string
    taskId: string
    platform?: string
    model?: string
    modelLabel?: string
    aspect?: string
    n?: number
  }>('/v1/agents/generation-retry', {
    method: 'POST',
    body: JSON.stringify({ taskId }),
  })
}

/** POST `/v1/agents/runs/stream`，按 SSE 事件回调 */
export function streamAgentRun(
  body: AgentRunRequest,
  handlers: AgentRunStreamHandlers,
): AgentRunStreamHandle {
  return openAgentRunStream(body, handlers)
}

/** POST `/v1/agents/pending-actions/confirm/stream`，确认后续跑并推送 SSE */
export function streamAgentConfirm(
  body: AgentConfirmStreamBody,
  handlers: AgentRunStreamHandlers,
): AgentRunStreamHandle {
  return openAgentConfirmStream(body, handlers)
}
